import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetHeaderDescription,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { NumericSpinnerInput } from '@/components/inputs/NumericSpinnerInput'
import { Button } from '@/components/Button'
import cx from 'classnames'
import styles from './BookkeepingCorrectCycleDialog.module.css'

export interface BookkeepingCorrectCycleDialogProps {
  showing: boolean
  onClose: () => unknown
  onSubmit: () => unknown
  isSubmitPending: boolean
  cycleNumber: number
  itemCount: number
  children?: ReactNode
}

export function BookkeepingCorrectCycleDialog({
  showing,
  onClose,
  onSubmit,
  isSubmitPending,
  cycleNumber,
  itemCount,
  children,
}: BookkeepingCorrectCycleDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader shellSize="sm" withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetTitle large>
            Make corrections to cycle {cycleNumber}
          </ModalSheetTitle>
          <ModalSheetHeaderDescription>
            Review and adjust the amount completed during this cycle.
          </ModalSheetHeaderDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock />
      </ModalSheetContent>
      <ModalSheetContent>
        <WorkItemsHeaders count={itemCount} />
        {children}
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            isDisabled={isSubmitPending}
            testId="correct-cycle-close-button"
          />
          <Button
            label="Correct Cycle"
            variant="primary"
            testId="correct-cycle-submit-button"
            onPress={onSubmit}
            isPending={isSubmitPending}
            isDisabled={isSubmitPending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

interface WorkItemsHeaderProps {
  count: number
}

function WorkItemsHeaders({ count }: WorkItemsHeaderProps) {
  return (
    <div className={styles.workItemsHeadersContainer}>
      <div className={cx(styles.workItemHeader, styles.service)}>
        Services Items <span>({count})</span>
      </div>
      <div className={cx(styles.workItemHeader, styles.completed)}>
        Completed
      </div>
      <div className={cx(styles.workItemHeader, styles.corrected)}>
        Correct amount
      </div>
    </div>
  )
}

export interface BookkeepingCorrectCycleDialogWorkItemRowProps {
  name: string
  description: string
  completedCount: number
  min: number
  max: number
  correctAmount: number
  isInvalid: boolean
  onCorrectAmountChange: (value: number | null) => unknown
}

export function BookkeepingCorrectCycleDialogWorkItemRow({
  name,
  description,
  completedCount,
  min,
  max,
  correctAmount,
  isInvalid,
  onCorrectAmountChange,
}: BookkeepingCorrectCycleDialogWorkItemRowProps) {
  return (
    <div
      className={styles.workItemRow}
      data-test={`correct-cycle-work-item-${name}`}
    >
      <div
        className={cx(
          styles.workItemCell,
          styles.service,
          styles.serviceContainer,
        )}
      >
        <div className={styles.serviceName}>{name}</div>
        <div className={styles.serviceDescription}>{description}</div>
      </div>
      <div
        className={cx(styles.workItemCell, styles.completed)}
        data-test={`correct-cycle-remaining-${name}`}
      >
        {completedCount}
      </div>
      <div className={cx(styles.workItemCell, styles.corrected)}>
        <NumericSpinnerInput
          min={min}
          max={max}
          value={correctAmount}
          invalid={isInvalid}
          onChange={onCorrectAmountChange}
          testId={`correct-cycle-work-item-input-${name}`}
        />
      </div>
    </div>
  )
}
